/* ==========================================================================
   TheHopeTarot — calculators.js (frontend)
   Renders the same form → loader → result in an inline container
   [data-calc-inline="<id>"] or an overlay opened from a [data-calc] tile.
   Calculation is separate from UI: numerology tools compute locally via
   window.HopeNumerology (numerology.js); Vedic tools call the serverless
   /api/calc. Both return the shared result contract and render identically.
   ========================================================================== */
(function () {
  'use strict';

  var API_ROOT = 'https://api.thehopetarot.org/api';
  var API_BASE = API_ROOT + '/calc';
  var LEAD_URL = API_ROOT + '/lead';

  /* ---------------- config ---------------- */
  /* Each calculator declares its calculation-specific fields. formFields()
     adds the shared required contact/birth fields in one place so individual
     calculators cannot accidentally omit them. */
  var CALCS = {
    flames: { name: 'FLAMES Calculator', local: true, fields: ['name', 'crush_name'], loader: 'Cancelling your letters…' },
    love: { name: 'Love Calculator', local: true, fields: ['name', 'partner'], loader: 'Comparing the two names…' },
    numerology: { name: 'Numerology Calculator', local: true, fields: ['name', 'dob', 'gender', 'phone'], loader: 'Calculating your numbers…' },
    personalYear: { name: 'Personal Year Number', local: true, fields: ['dob', 'year'], loader: 'Calculating your personal year…' },
    loShu: { name: 'Lo Shu Grid Calculator', local: true, fields: ['dob'], loader: 'Building your Lo Shu grid…' },
    nameNumerology: { name: 'Name Numerology Calculator', local: true, fields: ['birth_name', 'dob'], loader: 'Calculating your name numbers…' },
    favAlphabet: { name: 'Favorable Alphabet & Number', local: true, fields: ['dob'], loader: 'Finding your favourable letters…' },
    luckyColor: { name: 'Lucky Color Calculator', local: true, fields: ['dob'], loader: 'Finding your lucky colour…' },
    unluckyColor: { name: 'Unlucky Color Calculator', local: true, fields: ['dob'], loader: 'Checking your colours…' },
    luckyDates: { name: 'Lucky Dates Calculator', local: true, fields: ['dob'], loader: 'Marking your calendar…' },
    unluckyDates: { name: 'Unlucky Dates Calculator', local: true, fields: ['dob'], loader: 'Marking your calendar…' },
    vehicle: { name: 'Lucky Vehicle Number', local: true, fields: ['dob', 'vehicle'], loader: 'Checking your vehicle number…' },
    rudraksha: { name: 'Lucky Rudraksha Calculator', local: true, fields: ['dob'], loader: 'Finding your Rudraksha…' },
    kundli: { name: 'Kundli Calculator', local: false, fields: ['dob', 'time_required', 'place', 'phone'], loader: 'Calculating your birth chart…' },
    nakshatra: { name: 'Nakshatra Calculator', local: false, fields: ['dob', 'time', 'place', 'phone'], loader: 'Finding your Nakshatra…' },
    moonSign: { name: 'Moon Sign Calculator', local: false, fields: ['dob', 'time', 'place', 'phone'], loader: 'Finding your Rashi…' },
    lagnaNavamsa: { name: 'Lagna Navamsa Calculator', local: false, fields: ['dob', 'time_required', 'place'], loader: 'Calculating your charts…' },
    predictive: { name: 'Predictive Personality', local: false, fields: ['dob', 'time', 'place'], loader: 'Analysing your chart…' },
    babyName: { name: 'Baby Name Generator', local: false, uiOnly: true, fields: ['letter', 'rashi', 'baby_gender', 'religion', 'phone'], loader: 'Preparing your name choices…' },
    rahuKetu: { name: 'Rahu Ketu Calculator', local: false, fields: ['dob', 'time', 'place'], loader: 'Calculating Rahu and Ketu…' },
    sadeSati: { name: 'Sade Sati Calculator', local: false, fields: ['dob', 'time', 'place', 'phone'], loader: 'Calculating your Sade Sati…' },
    lalKitab: { name: 'Lal Kitab Calculator', local: false, fields: ['dob', 'time_required', 'place', 'phone'], loader: 'Calculating your Lal Kitab chart…' },
    memorablePeriod: { name: 'Memorable Period of Life', local: false, fields: ['dob', 'time', 'place'], loader: 'Calculating your dasha periods…' },
    matchMaking: { name: 'Match Making Calculator', local: false, fields: ['first_role', 'dob', 'time', 'place', 'partner', 'partner_dob', 'partner_tob', 'partner_place', 'phone'], loader: 'Comparing the two charts…' }
  };

  var RASHI_OPTIONS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  var RELIGION_OPTIONS = ['No preference', 'Hindu', 'Muslim', 'Christian', 'Sikh', 'Jain', 'Buddhist', 'Parsi', 'Other'];
  var CITY_OPTIONS = ['New Delhi', 'Mumbai', 'Bengaluru', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Surat', 'Kanpur', 'Nagpur', 'Indore', 'Bhopal', 'Patna', 'Vadodara', 'Chandigarh', 'Guwahati', 'Amritsar', 'Ranchi', 'Varanasi', 'Agra', 'Noida', 'Gurugram', 'Coimbatore', 'Kochi', 'Thiruvananthapuram', 'Visakhapatnam', 'London', 'New York', 'Toronto', 'Dubai', 'Singapore', 'Sydney', 'Los Angeles', 'Chicago', 'Kathmandu', 'Colombo', 'Dhaka'];

  function optionHTML(values, emptyLabel) {
    return '<option value="">' + emptyLabel + '</option>' + values.map(function (value) {
      return '<option value="' + esc(value) + '">' + esc(value) + '</option>';
    }).join('');
  }

  function fieldHTML(key) {
    switch (key) {
      case 'name': return '<label class="calc-field"><span>Your Name</span><input type="text" name="name" placeholder="Enter your name" autocomplete="name" required></label>';
      case 'birth_name': return '<label class="calc-field"><span>Full birth name</span><input type="text" name="name" placeholder="Enter the full name given at birth" autocomplete="name" required></label>';
      case 'crush_name': return '<label class="calc-field"><span>Your crush\'s name</span><input type="text" name="partner" placeholder="Enter their name" required></label>';
      case 'partner': return '<label class="calc-field"><span>Partner\'s Name</span><input type="text" name="partner" placeholder="Enter their name" required></label>';
      case 'dob': return '<label class="calc-field"><span>Date of birth</span><input type="date" name="dob" required></label>';
      case 'time': return '<label class="calc-field"><span>Time of birth</span><input type="time" name="tob"><small>Optional, but recommended for a more precise result.</small></label>';
      case 'time_required': return '<label class="calc-field"><span>Time of birth</span><input type="time" name="tob" required><small>The recorded birth time is needed for the Ascendant and houses.</small></label>';
      case 'place': return cityFieldHTML('place', 'Place of birth');
      case 'phone': return '<label class="calc-field"><span>Phone / WhatsApp number</span><input type="tel" name="phone" placeholder="Enter your Phone or WhatsApp number" inputmode="tel" autocomplete="tel" pattern="[+0-9 \\(\\)\\-]{8,20}" required><small>Required to continue. Your result appears immediately on this page.</small></label>';
      case 'vehicle': return '<label class="calc-field"><span>Vehicle number (last 4 digits)</span><input type="text" name="vehicle" placeholder="e.g. 3245" inputmode="numeric" pattern="[0-9]{1,4}" maxlength="4" required><small>Just the last four digits of your plate.</small></label>';
      case 'year': return '<label class="calc-field"><span>Year to check</span><input type="number" name="year" placeholder="' + new Date().getFullYear() + '" min="1900" max="2100"><small>Optional - defaults to this year.</small></label>';
      case 'partner_dob': return '<label class="calc-field"><span>Partner\'s date of birth</span><input type="date" name="partner_dob" required></label>';
      case 'partner_tob': return '<label class="calc-field"><span>Partner\'s time of birth</span><input type="time" name="partner_tob"><small>Optional, but recommended for a more precise match.</small></label>';
      case 'partner_place': return cityFieldHTML('partner_place', 'Partner\'s place of birth');
      case 'first_role': return '<label class="calc-field"><span>Your role for Guna Milan</span><select name="first_role" required><option value="">Select bride or groom</option><option value="bride">Bride</option><option value="groom">Groom</option></select><small>Traditional tables use bride and groom roles. Your partner\'s role is assigned automatically.</small></label>';
      case 'letter': return '<label class="calc-field"><span>Select letter</span><select name="letter" required>' + optionHTML('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''), 'Select a letter') + '</select></label>';
      case 'rashi': return '<label class="calc-field"><span>Select Rashi</span><select name="rashi" required>' + optionHTML(RASHI_OPTIONS, 'Select a Rashi') + '</select></label>';
      case 'gender': return '<label class="calc-field"><span>Gender</span><select name="gender" required>' + optionHTML(['Male', 'Female', 'Other'], 'Select gender') + '</select></label>';
      case 'baby_gender': return '<label class="calc-field"><span>Gender</span><select name="gender" required>' + optionHTML(['Girl', 'Boy', 'Gender-neutral'], 'Select gender') + '</select></label>';
      case 'religion': return '<label class="calc-field"><span>Religion preference</span><select name="religion" required>' + optionHTML(RELIGION_OPTIONS, 'Select a preference') + '</select><small>Choose “No preference” if religion should not limit the suggestions.</small></label>';
      default: return '';
    }
  }

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  /* Place-of-birth field with live city autocomplete (any city, no hard-coded list).
     Visible combobox is required so the field can't be left blank; the hidden input
     (name=<field>) holds "Name|lat|lon|zone" once a suggestion is picked. If the user
     types a city but doesn't pick, submit falls back to the typed name — the backend
     resolves plain names too. Behaviour: assets/city-autocomplete.js. */
  var acSeq = 0;
  function cityFieldHTML(fieldName, labelText) {
    var idb = 'calc-ac-' + (++acSeq);
    var lid = idb + '-list';
    return '<label class="calc-field city-ac" data-city-ac>' +
      '<span>' + labelText + '</span>' +
      '<span class="city-ac__box">' +
        '<input class="city-ac__input" id="' + idb + '" type="text" data-city-search autocomplete="off" spellcheck="false" role="combobox" aria-expanded="false" aria-autocomplete="list" aria-controls="' + lid + '" placeholder="Start typing a city…" required>' +
        '<input type="hidden" name="' + fieldName + '" data-city-value>' +
        '<ul class="city-ac__list" id="' + lid + '" data-city-list role="listbox" hidden></ul>' +
      '</span>' +
    '</label>';
  }

  /* ---------------- form ---------------- */
  function formFields(id, cfg) {
    var declared = (cfg.fields || []).slice();
    var isBabyName = id === 'babyName';
    if (id === 'love' || id === 'flames') return declared.concat(['phone']);
    var primaryName = declared.indexOf('birth_name') !== -1 ? 'birth_name' : 'name';
    var specific = declared.filter(function (key) {
      return key !== 'name' && key !== 'birth_name' && key !== 'dob' && key !== 'phone';
    });

    if (isBabyName) return specific.concat(['phone']);
    return [primaryName, 'dob'].concat(specific, ['phone']);
  }

  function formHTML(id, cfg) {
    var fields = formFields(id, cfg).map(fieldHTML).join('');
    return '<form class="calc-form" novalidate onsubmit="return false;">' +
      '<div class="calc-fields">' +
        fields +
      '</div>' +
      '<button class="btn btn--primary calc-form__submit" type="submit">Get My Result</button>' +
      '<p class="calc-disclaimer">For guidance &amp; self-reflection — not a substitute for professional advice.</p>' +
    '</form>';
  }

  function renderForm(id, cfg, mount, mode) {
    if (cfg.uiOnly) {
      mount.innerHTML = '<p class="calc-unavailable" role="status">Baby name suggestions are not available yet. Please check back once the reviewed name catalogue is ready.</p>';
      return;
    }
    mount.innerHTML = formHTML(id, cfg);
    var form = mount.querySelector('.calc-form');
    if (window.CityAutocomplete) window.CityAutocomplete.init(mount);
    var draft = mount._calcDraft || {};
    Array.prototype.forEach.call(form.elements, function (field) {
      if (Object.prototype.hasOwnProperty.call(draft, field.name)) field.value = draft[field.name];
      if (field.type === 'date') field.max = new Date().toLocaleDateString('en-CA');
    });
    form.querySelectorAll('[data-city-ac]').forEach(function (ac) {
      var hidden = ac.querySelector('[data-city-value]'), search = ac.querySelector('[data-city-search]');
      if (hidden && search && hidden.value) search.value = hidden.value.split('|')[0];
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // Only accept a real, resolved place. If the field has typed text but no
      // suggestion was picked (so no coordinates), ask the user to choose from the
      // list instead of sending an unverified name.
      var cityBad = null;
      form.querySelectorAll('[data-city-ac]').forEach(function (ac) {
        var vis = ac.querySelector('[data-city-search]');
        var hid = ac.querySelector('[data-city-value]');
        if (vis && hid && vis.value.trim() && !hid.value) {
          vis.classList.add('is-invalid');
          if (!cityBad) cityBad = vis;
          vis.setCustomValidity('Please choose a city from the suggestions.');
          var clr = function () { if (hid.value || !vis.value.trim()) { vis.classList.remove('is-invalid'); vis.setCustomValidity(''); } };
          vis.addEventListener('input', clr); vis.addEventListener('change', clr);
        }
      });
      if (cityBad) { cityBad.focus(); cityBad.reportValidity(); return; }
      var data = {}; new FormData(e.target).forEach(function (v, k) { data[k] = String(v).trim(); });
      var missing = form.querySelectorAll('[required]');
      var bad = false;
      missing.forEach(function (inp) {
        if (!inp.value || !inp.checkValidity()) {
          bad = true;
          inp.classList.add('is-invalid');
          var clearInvalid = function () { if (inp.checkValidity()) inp.classList.remove('is-invalid'); };
          inp.addEventListener('input', clearInvalid);
          inp.addEventListener('change', clearInvalid);
        }
      });
      if (bad) {
        var firstInvalid = form.querySelector('.is-invalid');
        if (firstInvalid) {
          firstInvalid.focus();
          firstInvalid.reportValidity();
        }
        return;
      }
      runCalc(id, cfg, data, mount, mode);
    });
  }

  /* ---------------- run (local numerology OR API) ---------------- */
  function runCalc(id, cfg, data, mount, mode) {
    mount._calcDraft = Object.assign({}, data);
    var runToken = (mount._calcRunToken || 0) + 1;
    mount._calcRunToken = runToken;
    mount.innerHTML = '<div class="calc-loader"><span class="calc-loader__spin"></span><p class="calc-loader__cap">' + esc(cfg.loader || 'Working…') + '</p></div>';
    var t0 = Date.now();
    function finish(out) {
      var wait = 0;
      setTimeout(function () {
        if (mount._calcRunToken !== runToken) return;
        var validResult = out && out.ok && out.result && typeof out.result.headline === 'string';
        if (mode === 'inline') {
          renderForm(id, cfg, mount, 'inline');
          openOutcomeOverlay(id, cfg, validResult ? out : { ok: false, error: out && out.error }, data, mount);
        } else if (validResult) {
          renderResult(id, cfg, out.result, data, mount, mode);
        } else {
          renderError(id, cfg, out && out.error, data, mount, mode);
        }
      }, wait);
    }
    // UI preview only: the approved internal name catalogue will replace this
    // result after the form design has been finalised.
    if (cfg.uiOnly && id === 'babyName') {
      // The reviewed name catalogue isn't connected yet, so this tool can't return
      // real names. Mark it clearly unavailable instead of showing a placeholder
      // as if it were a finished result.
      finish({ ok: false, error: 'Baby name suggestions aren’t available yet — our reviewed name catalogue is being connected. Please check back soon.' });
      return;
    }
    if (cfg.local && window.HopeNumerology && window.HopeNumerology.TOOLS[id]) {
      try {
        var invalid = window.HopeNumerology.validate(id, data);
        if (invalid) { finish(Object.assign({ ok: false }, invalid)); return; }
        if (!data.dob && id !== 'flames' && id !== 'love') { finish({ ok: false, error: 'Please enter a valid date of birth.' }); return; }
        if ((id === 'flames' || id === 'love') && !data.partner) { finish({ ok: false, error: "Please enter the other person's name." }); return; }
        if (id === 'vehicle' && !/\d/.test(data.vehicle || '')) { finish({ ok: false, error: 'Please enter your vehicle number.' }); return; }
        var localResult = window.HopeNumerology.TOOLS[id](data);
        finish({ ok: true, result: localResult });
        storeLead(id, data, localResult); // client-computed tools: persist to Supabase via backend
      } catch (err) { finish({ ok: false, error: 'Could not compute — please check your inputs.' }); }
      return;
    }
    // Vedic → API
    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, 20000);
    fetch(API_BASE, { signal: controller.signal, method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tool: id, inputs: data }) })
      .then(function (r) { clearTimeout(timeout); return r.json(); })
      .then(function (out) { finish(out); })
      .catch(function () { clearTimeout(timeout); finish({ ok: false, error: 'network' }); });
  }

  /* ---------------- result ---------------- */
  function metricsHTML(metrics) {
    if (!metrics || !metrics.length) return '';
    return '<div class="calc-metrics">' + metrics.map(function (m) {
      return '<div class="calc-metric"><span class="calc-metric__lbl">' + esc(m.label) + '</span><span class="calc-metric__val">' + esc(m.value) + '</span></div>';
    }).join('') + '</div>';
  }

  var RASHIS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  function visualHTML(v) {
    if (!v) return '';
    var d = v.data || {};
    switch (v.type) {
      case 'badges': return '<div class="calc-row' + (d.length > 2 ? ' calc-row--3' : '') + '">' + d.map(function (x) { return '<div class="calc-badge"><span class="calc-badge__num">' + x.n + '</span><span class="calc-badge__lbl">' + esc(x.label) + '</span></div>'; }).join('') + '</div>';
      case 'wheel': return '<div class="calc-year-wheel">' + [1, 2, 3, 4, 5, 6, 7, 8, 9].map(function (n) { return '<span class="calc-year-seg' + (n === d.current ? ' is-on' : '') + '">' + n + '</span>'; }).join('') + '</div>';
      case 'loshu': var pos = [4, 9, 2, 3, 5, 7, 8, 1, 6]; return '<div class="calc-loshu">' + pos.map(function (n) { var c = (d.count || {})[n] || 0; return '<span class="calc-loshu__cell' + (c ? ' is-filled' : ' is-empty') + '">' + (c ? String(n).repeat(c) : '·') + '</span>'; }).join('') + '</div>';
      case 'letters': return '<div class="calc-letters">' + (d.letters || []).map(function (l) { return '<span class="calc-letter">' + esc(l) + '</span>'; }).join('') + '</div>';
      case 'swatch': return '<div class="calc-swatch-big" style="background:' + esc(d.hex) + '"></div>';
      case 'dates': var arr = d.good || d.bad || []; var cls = d.good ? 'is-good' : 'is-bad'; return '<div class="calc-cal">' + arr.map(function (x) { return '<span class="calc-cal__d ' + cls + '">' + x + '</span>'; }).join('') + '</div>';
      case 'verdict': var tone = d.tone === 'good' ? 'is-good' : d.tone === 'bad' ? 'is-bad' : d.tone === 'mid' ? 'is-mid' : (d.verdict === 'Friendly' ? 'is-good' : d.verdict === 'Avoid' ? 'is-bad' : 'is-mid'); return '<div class="calc-verdict ' + tone + '">' + esc(d.verdict) + '</div>';
      case 'bead': return '<div class="calc-bead"><span class="calc-bead__ct">' + d.mukhi + '</span><span class="calc-bead__lbl">Mukhi</span></div>';
      case 'flames': return '<div class="calc-flames">' + 'FLAMES'.split('').map(function (c) { return '<span class="calc-flames__l' + (d.word && d.word[0] === c ? ' is-on' : '') + '">' + c + '</span>'; }).join('') + '</div>';
      case 'meter': return '<div class="calc-meter"><div class="calc-meter__track"><div class="calc-meter__fill" style="width:' + d.pct + '%"></div></div><span class="calc-meter__val">' + d.pct + '%</span></div>';
      case 'timeline': return '<div class="calc-tl">' + (d.periods || []).map(function (p) { return '<span class="calc-tl__seg' + (p.current ? ' is-on' : '') + '"><b>' + esc(p.lord) + '</b><small>' + p.from + '</small></span>'; }).join('') + '</div>';
      case 'score': var pctv = Math.round((d.score / d.max) * 100); return '<div class="calc-score"><div class="calc-score__dial" style="--p:' + pctv + '"><span>' + d.score + '<small>/' + d.max + '</small></span></div></div>';
      case 'gunamilan':
        var gunaPct = Math.round((d.score / (d.max || 36)) * 100);
        var gunaRows = Object.keys(d.kootas || {}).map(function (key) {
          return { label: key, value: d.kootas[key] + ' / ' + ((d.kootasMax || {})[key] || '') };
        });
        var roleText = d.roles ? '<p class="calc-result__text">You: ' + esc(d.roles.you) + ' · Partner: ' + esc(d.roles.partner) + '</p>' : '';
        return '<div class="calc-score"><div class="calc-score__dial" style="--p:' + gunaPct + '"><span>' + d.score + '<small>/' + (d.max || 36) + '</small></span></div></div>' + roleText + metricsHTML(gunaRows);
      case 'chart': return chartHTML(d);
      case 'numgrid': return '<div class="calc-numgrid">' + (d.items || []).map(function (it) {
        return '<div class="calc-numcard"><span class="calc-numcard__lbl">' + esc(it.label) + '</span><span class="calc-numcard__nums">' + (it.nums || []).map(function (n) { return '<span class="calc-numcard__n">' + n + '</span>'; }).join('') + '</span></div>';
      }).join('') + '</div>';
      case 'calendar': return calendarHTML(d);
      default: return '';
    }
  }
  /* Full month grid (current month) with the given day-numbers highlighted. */
  function calendarHTML(d) {
    var hi = {}; (d.highlight || []).forEach(function (x) { hi[x] = 1; });
    var tone = d.tone === 'bad' ? 'is-bad' : 'is-good';
    var now = new Date(), y = now.getFullYear(), m = now.getMonth();
    var first = new Date(y, m, 1).getDay(), days = new Date(y, m + 1, 0).getDate();
    var head = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(function (w) { return '<span class="calc-month__dow">' + w + '</span>'; }).join('');
    var cells = '';
    for (var i = 0; i < first; i++) cells += '<span class="calc-month__cell is-empty"></span>';
    for (var dnum = 1; dnum <= days; dnum++) { cells += '<span class="calc-month__cell' + (hi[dnum] ? ' ' + tone : '') + '">' + dnum + '</span>'; }
    return '<div class="calc-month"><div class="calc-month__grid">' + head + cells + '</div></div>';
  }
  function chartHTML(d) {
    var byHouse = {}; var lag = d.lagna || 0;
    var ABBR = { Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me', Jupiter: 'Ju', Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke' };
    Object.keys(d.planets || {}).forEach(function (k) { if (ABBR[k]) { var h = ((d.planets[k] - lag) + 12) % 12; (byHouse[h] = byHouse[h] || []).push(ABBR[k]); } });
    var cells = '';
    for (var h = 0; h < 12; h++) { cells += '<div class="calc-chart__cell"><span class="calc-chart__h">' + (h + 1) + '</span><span class="calc-chart__p">' + ((byHouse[h] || []).join(' ')) + '</span></div>'; }
    return '<div class="calc-chart"><div class="calc-chart__lagna">Lagna: ' + esc(RASHIS[lag]) + '</div><div class="calc-chart__grid">' + cells + '</div></div>';
  }

  function actionsHTML(mode) {
    return '<div class="calc-actions">' +
      '<button class="calc-action" type="button" data-calc-restart>Edit Details</button>' +
      (mode === 'overlay' || mode === 'result-overlay' ? '<button class="calc-action" type="button" data-calc-close2>Close</button>' : '') +
      '<a class="calc-action calc-action--primary" href="/pages/reports">Explore Reports</a>' +
    '</div>';
  }

  function presentCopy(value) {
    if (typeof value === 'string') return value
      .replace(/Shared health, fortune and destiny[^.]*\./g, 'Traditional birth-star compatibility.')
      .replace(/Health and progeny;[^.]*\./g, 'Traditional Nadi compatibility.')
      .replace(/Health & progeny/g, 'Nadi compatibility')
      .replace(/quick, healing and pioneering/g, 'quick and pioneering')
      .replace(/healing, secretive and independent/g, 'private and independent');
    if (Array.isArray(value)) return value.map(presentCopy);
    if (value && typeof value === 'object') { var next = {}; Object.keys(value).forEach(function(key) { next[key] = presentCopy(value[key]); }); return next; }
    return value;
  }
  function renderResult(id, cfg, res, data, mount, mode) {
    res = presentCopy(res);
    mount._calcDraft = Object.assign({}, data);
    var html = '<div class="calc-result" role="status" aria-live="polite">' +
      '<span class="calc-result__eyebrow">Result</span>' +
      '<h3 class="calc-result__headline" tabindex="-1">' + esc(res.headline) + '</h3>' +
      (res.visual ? '<div class="calc-result__visual">' + visualHTML(res.visual) + '</div>' : '') +
      (res.overview ? '<p class="calc-result__text">' + esc(res.overview) + '</p>' : '') +
      metricsHTML(res.metrics) +
      (res.meta && res.meta.approx ? '<p class="calc-approx">' + esc(res.meta.note || 'This estimate uses noon where a birth time was not provided. The result may change with the recorded time.') + '</p>' : '') +
      (res.meta && res.meta.dateNote ? '<p class="calc-approx">' + esc(res.meta.dateNote) + '</p>' : '') +
      (res.meta && res.meta.timeNote ? '<p class="calc-approx">' + esc(res.meta.timeNote) + '</p>' : '') +
      (res.disclaimer ? '<p class="calc-disclaimer">' + esc(res.disclaimer) + '</p>' : '') +
      actionsHTML(mode) + '</div>';
    mount.innerHTML = html;
    if (mount.scrollTo) mount.scrollTo(0, 0);
    bindActions(id, cfg, mount, mode);
  }

  function renderError(id, cfg, error, data, mount, mode) {
    mount._calcDraft = Object.assign({}, data);
    var message = typeof error === 'string' && error !== 'network'
      ? error
      : 'We could not calculate your result right now. Please check your connection and try again.';
    mount.innerHTML = '<div class="calc-result calc-result--error" role="alert">' +
      '<span class="calc-result__eyebrow">Result unavailable</span>' +
      '<h3 class="calc-result__headline" tabindex="-1">Please try again</h3>' +
      '<p class="calc-result__text">' + esc(message) + '</p>' +
      '<div class="calc-actions"><button class="calc-action calc-action--primary" type="button" data-calc-restart>Try Again</button>' +
      (mode === 'overlay' || mode === 'result-overlay' ? '<button class="calc-action" type="button" data-calc-close2>Close</button>' : '') +
      '</div></div>';
    bindActions(id, cfg, mount, mode);
  }

  function bindActions(id, cfg, mount, mode) {
    var r = mount.querySelector('[data-calc-restart]');
    if (r) r.addEventListener('click', function () {
      if (mode === 'result-overlay') {
        var origin = resultOrigin;
        closeOverlay();
        if (origin) {
          origin.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var first = origin.querySelector('input, select, button');
          if (first) setTimeout(function () { first.focus(); }, 80);
        }
        return;
      }
      renderForm(id, cfg, mount, mode);
      if (mode === 'inline') mount.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    var c = mount.querySelector('[data-calc-close2]');
    if (c) c.addEventListener('click', closeOverlay);
  }

  /* best-effort persist to Supabase (via the backend) for client-computed
     numerology tools. Vedic tools are persisted server-side by /api/calc.
     Fire-and-forget: never blocks or errors the UI. */
  function storeLead(id, data, result) {
    try {
      fetch(LEAD_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ kind: 'calculator', tool: id, inputs: data, result: result })
      }).catch(function () {});
    } catch (e) {}
  }


  /* ---------------- inline mount ---------------- */
  function initInline(scope) {
    (scope || document).querySelectorAll('[data-calc-inline]').forEach(function (el) {
      if (el.dataset.calcBound) return; el.dataset.calcBound = '1';
      var id = el.getAttribute('data-calc-inline'); var cfg = CALCS[id]; if (!cfg) return;
      renderForm(id, cfg, el, 'inline');
    });
  }

  /* ---------------- overlay ---------------- */
  var overlay, lastFocus, resultOrigin;
  /* focusable elements currently inside a container (skips hidden ones) */
  function focusable(container) {
    return Array.prototype.slice.call(container.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetWidth || el.offsetHeight || el === document.activeElement; });
  }
  /* make the rest of the page inert (blocks Tab + assistive tech) while the
     dialog is open; fully reversed on close. Focus trap below covers browsers
     without inert support. */
  function setBackgroundInert(on) {
    if (!overlay) return;
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === overlay) return;
      if (on) {
        if (!el.hasAttribute('inert') && !el.hasAttribute('data-calc-inert')) { el.setAttribute('data-calc-inert', ''); el.setAttribute('inert', ''); }
      } else if (el.hasAttribute('data-calc-inert')) {
        el.removeAttribute('inert'); el.removeAttribute('data-calc-inert');
      }
    });
  }
  function buildOverlay() {
    if (overlay) return overlay;
    overlay = document.createElement('div'); overlay.className = 'calc-overlay'; overlay.setAttribute('hidden', '');
    overlay.innerHTML = '<div class="calc-overlay__backdrop" data-calc-close></div>' +
      '<div class="calc-modal" role="dialog" aria-modal="true" tabindex="-1" aria-labelledby="calc-title" aria-describedby="calc-subtitle">' +
        '<button class="calc-modal__close" type="button" data-calc-close aria-label="Close">&times;</button>' +
        '<div class="calc-modal__head"><div><h2 class="calc-modal__title" id="calc-title" data-calc-name></h2><p class="calc-modal__sub" id="calc-subtitle" data-calc-sub></p></div></div>' +
        '<div class="calc-modal__body" data-calc-body></div></div>';
    document.body.appendChild(overlay);
    overlay.querySelectorAll('[data-calc-close]').forEach(function (el) { el.addEventListener('click', closeOverlay); });
    document.addEventListener('keydown', function (e) {
      if (overlay.hasAttribute('hidden')) return;
      if (e.key === 'Escape') { closeOverlay(); return; }
      if (e.key !== 'Tab') return;
      var modal = overlay.querySelector('.calc-modal');
      var items = focusable(modal);
      if (!items.length) { e.preventDefault(); modal.focus(); return; }
      var firstEl = items[0], lastEl = items[items.length - 1], active = document.activeElement;
      if (e.shiftKey) {
        if (active === firstEl || !modal.contains(active)) { e.preventDefault(); lastEl.focus(); }
      } else if (active === lastEl || !modal.contains(active)) { e.preventDefault(); firstEl.focus(); }
    });
    return overlay;
  }
  function closeOverlay() {
    if (!overlay) return;
    overlay.setAttribute('hidden', '');
    var body = overlay.querySelector('[data-calc-body]');
    body._calcRunToken = (body._calcRunToken || 0) + 1;
    overlay.querySelector('.calc-modal').classList.remove('calc-modal--result');
    document.body.classList.remove('calc-open');
    setBackgroundInert(false);
    resultOrigin = null;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  function openOutcomeOverlay(id, cfg, out, data, origin) {
    resultOrigin = origin;
    lastFocus = origin.querySelector('input, select, button') || document.activeElement;
    buildOverlay();
    overlay.querySelector('[data-calc-name]').textContent = cfg.name;
    overlay.querySelector('[data-calc-sub]').textContent = out && out.ok
      ? 'Your result is ready now.'
      : 'We could not complete this calculation.';
    overlay.querySelector('.calc-modal').classList.add('calc-modal--result');
    overlay.querySelector('.calc-modal').style.setProperty('--calc-accent', '#86220f');
    var body = overlay.querySelector('[data-calc-body]');
    if (out && out.ok) renderResult(id, cfg, out.result, data, body, 'result-overlay');
    else renderError(id, cfg, out && out.error, data, body, 'result-overlay');
    overlay.removeAttribute('hidden');
    document.body.classList.add('calc-open');
    setBackgroundInert(true);
    var heading = body.querySelector('.calc-result__headline');
    setTimeout(function () { (heading || overlay.querySelector('.calc-modal')).focus(); }, 60);
  }
  function openCalc(id, meta) {
    var cfg = CALCS[id]; if (!cfg) return; lastFocus = document.activeElement; resultOrigin = null; buildOverlay();
    overlay.querySelector('.calc-modal').classList.remove('calc-modal--result');
    overlay.querySelector('[data-calc-name]').textContent = (meta && meta.title) || cfg.name;
    overlay.querySelector('[data-calc-sub]').textContent = (meta && meta.desc) || '';
    overlay.querySelector('.calc-modal').style.setProperty('--calc-accent', (meta && meta.accent) || '#86220f');
    var body = overlay.querySelector('[data-calc-body]');
    body._calcDraft = {};
    renderForm(id, cfg, body, 'overlay');
    overlay.removeAttribute('hidden'); document.body.classList.add('calc-open');
    setBackgroundInert(true);
    var f = overlay.querySelector('input, select');
    setTimeout(function () { (f || overlay.querySelector('.calc-modal')).focus(); }, 60);
  }
  function initTiles(scope) {
    (scope || document).querySelectorAll('[data-calc]').forEach(function (tile) {
      if (tile.dataset.calcBound) return; tile.dataset.calcBound = '1';
      tile.addEventListener('click', function (e) { e.preventDefault(); openCalc(tile.getAttribute('data-calc'), { title: tile.getAttribute('data-calc-title'), desc: tile.getAttribute('data-calc-desc'), accent: tile.getAttribute('data-calc-accent') }); });
    });
  }

  function boot(scope) { initInline(scope); initTiles(scope); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  window.HopeCalc = { open: openCalc, init: boot };
})();
